/*
  ConBox custom size builder (replaces the EASIFY options app).

  Pricing model: each custom product has one option, "Size band", with one variant per volume
  bucket ("Up to 0.35 m³"). The customer enters W x H x D, we compute the volume, pick the
  matching variant and add the measurements as line item properties (same names EASIFY used,
  so ConBox Ops keeps parsing them). The variant price itself is the charge: no cart transform.

  The pure functions at the top are shared with the Node tests and the variant plan script.
*/
(function (root) {
  'use strict';

  var DEFAULTS = {
    basePrice: 399, // AUD, the old product price
    ratePerM3: 800, // AUD per m³: Ben's formula W*H*D/1.25e9 * 1000 (live value comes from ConBox Ops)
    // Bands (Ben, 19 Oct 2026): every 0.05 m³ ($48 steps at $960/m³) from 0.05 up to 5.25 m³ = 105
    // variants. The smallest possible custom (300 x 300 x 280 mm = 0.0252 m³) is in "Up to 0.05 m³".
    tiers: [{ upToM3: 5.25, stepM3: 0.05 }],
    startM3: 0.05,
    bandStepM3: 0.025, // only used when tiers is empty
    rounding: 'up', // 'up' = next band (never undercharges), 'nearest'
    maxBands: 210, // only used when tiers is empty (store limit is 2048 variants)
    titleDecimals: 2,
    limitsMm: { width: [300, 1700], height: [300, 2200], depth: [280, 1400] },
  };

  var MM3_PER_M3 = 1e9;

  function toMm(value, unit) {
    var n = parseFloat(String(value == null ? '' : value).replace(',', '.'));
    if (!isFinite(n)) return null;
    return Math.round(unit === 'cm' ? n * 10 : n); // whole millimetres keep the maths exact
  }

  function volumeMm3(dimsMm) {
    return dimsMm.width * dimsMm.height * dimsMm.depth;
  }

  // Band number (1 = the smallest bucket) for a volume, using integer maths so a volume that
  // sits exactly on a boundary is never pushed into the next band by floating point error.
  // Band upper edges in whole mm³ (integers keep boundaries exact). Cached per config.
  var _edgeCache = {};
  function bandEdges(cfg) {
    var key = JSON.stringify([cfg.tiers, cfg.startM3, cfg.bandStepM3, cfg.maxBands]);
    if (_edgeCache[key]) return _edgeCache[key];
    var e = [];
    if (cfg.tiers && cfg.tiers.length) {
      var cur = Math.round((cfg.startM3 || cfg.tiers[0].stepM3) * MM3_PER_M3);
      cfg.tiers.forEach(function (t) {
        var step = Math.round(t.stepM3 * MM3_PER_M3), top = Math.round(t.upToM3 * MM3_PER_M3);
        if (e.length) cur = e[e.length - 1] + step; // each tier starts one of its own steps above the last edge
        for (; cur <= top; cur += step) e.push(cur);
      });
    } else {
      var st = Math.round(cfg.bandStepM3 * MM3_PER_M3);
      for (var i = 1; i <= cfg.maxBands; i++) e.push(i * st);
    }
    return (_edgeCache[key] = e);
  }
  function bandCount(cfg) { return bandEdges(cfg).length; }

  function bandIndex(volMm3, cfg) {
    var e = bandEdges(cfg);
    for (var i = 0; i < e.length; i++) {
      if (volMm3 <= e[i]) {
        if (cfg.rounding === 'nearest' && i > 0 && volMm3 - e[i - 1] < e[i] - volMm3) return i; // closer to the band below
        return i + 1;
      }
    }
    return e.length + 1; // above the top band
  }

  function bandMaxM3(idx, cfg) {
    var e = bandEdges(cfg);
    var mm3 = idx <= e.length ? e[idx - 1] : e[e.length - 1];
    return Math.round(mm3 / 1e6) / 1000;
  }

  // Variant SKU per band, e.g. BB-CUSTOM-0030 = up to 0.030 m³; CB-CUSTOM-1050 = up to 1.050 m³.
  function bandSku(idx, baseSku, cfg) {
    return baseSku + '-' + String(Math.round(bandMaxM3(idx, cfg) * 1000)).padStart(4, '0');
  }

  function bandPrice(idx, cfg) {
    return Math.round((cfg.basePrice + cfg.ratePerM3 * bandMaxM3(idx, cfg)) * 100) / 100;
  }

  function bandTitle(idx, cfg) {
    return 'Up to ' + bandMaxM3(idx, cfg).toFixed(cfg.titleDecimals == null ? 2 : cfg.titleDecimals) + ' m³';
  }

  function parseBandTitle(title) {
    var m = String(title || '').match(/([0-9]+(?:\.[0-9]+)?)\s*m/);
    return m ? parseFloat(m[1]) : null;
  }

  // Validate dimensions (in mm). Returns a list of { field, message } in the customer's unit.
  function validate(dimsMm, unit, cfg) {
    var errors = [];
    var label = { width: 'Width', height: 'Height', depth: 'Depth' };
    var fmt = function (mm) { return unit === 'cm' ? mm / 10 + ' cm' : mm + ' mm'; };
    ['width', 'height', 'depth'].forEach(function (k) {
      var v = dimsMm[k];
      var lim = cfg.limitsMm[k];
      if (v == null || v <= 0) errors.push({ field: k, message: 'Enter the ' + label[k].toLowerCase() + '.' });
      else if (v < lim[0]) errors.push({ field: k, message: label[k] + ' must be at least ' + fmt(lim[0]) + '.' });
      else if (v > lim[1]) errors.push({ field: k, message: label[k] + ' can be at most ' + fmt(lim[1]) + '. For bigger sizes, contact us for a quote.' });
    });
    return errors;
  }

  // Full quote for a set of dimensions. variants: [{ id, title, price (cents), available }].
  function quote(input, cfg, variants) {
    cfg = Object.assign({}, DEFAULTS, cfg || {});
    var unit = input.unit === 'mm' ? 'mm' : 'cm';
    var dims = { width: toMm(input.width, unit), height: toMm(input.height, unit), depth: toMm(input.depth, unit) };
    var errors = validate(dims, unit, cfg);
    if (errors.length) return { ok: false, errors: errors, dimsMm: dims, unit: unit };
    var vol = volumeMm3(dims);
    var idx = bandIndex(vol, cfg);
    var res = { ok: true, errors: [], dimsMm: dims, unit: unit, volumeM3: vol / MM3_PER_M3, band: idx, bandMaxM3: bandMaxM3(idx, cfg), planPrice: bandPrice(idx, cfg) };
    if (idx > bandCount(cfg)) return Object.assign(res, { ok: false, errors: [{ field: 'volume', message: 'This size is larger than we can price online. Contact us for a quote.' }] });
    if (variants) {
      // The smallest variant whose band covers the volume (variants carry their max in the title).
      var match = null;
      variants.forEach(function (v) {
        var max = parseBandTitle(v.title);
        if (max == null || max + 1e-9 < res.bandMaxM3) return;
        if (!match || max < parseBandTitle(match.title)) match = v;
      });
      if (!match) return Object.assign(res, { ok: false, errors: [{ field: 'volume', message: 'No price band covers this size yet. Contact us for a quote.' }] });
      res.variant = match;
    }
    return res;
  }

  // Line item properties, named exactly as EASIFY did so ConBox Ops parses them unchanged.
  function lineProperties(q, choices, cfg) {
    cfg = Object.assign({}, DEFAULTS, cfg || {});
    var u = q.unit;
    var show = function (mm) { return (u === 'cm' ? +(mm / 10).toFixed(1) : mm) + ' ' + u; };
    var surcharge = q.variant ? q.variant.price / 100 - cfg.basePrice : q.planPrice - cfg.basePrice;
    var props = {};
    props['Measurement Unit'] = u;
    props['Measurements (' + u + ')-Width'] = show(q.dimsMm.width);
    props['Measurements (' + u + ')-Height'] = show(q.dimsMm.height);
    props['Measurements (' + u + ')-Depth'] = show(q.dimsMm.depth);
    // Price line for ConBox Ops (it reads the upcharge from here). Underscore = private, so customers never see it.
    props['_Measurements (' + u + ')'] = '[ +$' + surcharge.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ]';
    props['Colour'] = choices.colour;
    props['Top Lid'] = choices.lid;
    props['Delivery and lead-time'] = choices.leadTime;
    if (choices.notes && String(choices.notes).trim()) props['Additional Notes'] = String(choices.notes).trim();
    props['_volume_m3'] = q.volumeM3.toFixed(4);
    props['_size_band'] = bandTitle(q.band, cfg);
    props['_builder'] = 'conbox-size-builder v1';
    return props;
  }

  var api = { DEFAULTS: DEFAULTS, toMm: toMm, volumeMm3: volumeMm3, bandIndex: bandIndex, bandMaxM3: bandMaxM3, bandPrice: bandPrice, bandTitle: bandTitle, bandEdges: bandEdges, bandCount: bandCount, bandSku: bandSku, parseBandTitle: parseBandTitle, validate: validate, quote: quote, lineProperties: lineProperties };
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.ConboxSizeBuilder = api;

  // ---------------------------------------------------------------- browser widget
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  class CustomSizeBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var data = JSON.parse(this.querySelector('[data-csb-config]').textContent);
      this.cfg = Object.assign({}, DEFAULTS, data.config);
      this.variants = data.variants;
      this.leadTime = data.leadTime;
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('[name="id"]');
      this.propsBox = this.querySelector('[data-csb-props]');
      this.priceEl = this.querySelector('[data-csb-price]');
      this.volumeEl = this.querySelector('[data-csb-volume]');
      this.submit = this.form.querySelector('[type="submit"]');
      this.touched = {};
      this.addEventListener('input', this.update.bind(this));
      this.addEventListener('change', this.update.bind(this));
      this.addEventListener('focusout', (e) => { if (e.target.name && e.target.name.indexOf('csb-') === 0) { this.touched[e.target.name] = true; this.update(); } });
      // Capture phase on the wrapper runs before Dawn's product-form submit handler.
      this.addEventListener('submit', (e) => {
        ['csb-width', 'csb-height', 'csb-depth'].forEach((n) => (this.touched[n] = true));
        if (!this.update()) { e.preventDefault(); e.stopImmediatePropagation(); var bad = this.querySelector('[aria-invalid="true"]'); if (bad) bad.focus(); }
      }, true);
      this.update();
    }

    val(name) {
      var el = this.querySelector('[name="' + name + '"]:checked') || this.querySelector('[name="' + name + '"]');
      return el ? el.value : '';
    }

    update() {
      var unit = this.val('csb-unit');
      this.querySelectorAll('[data-csb-unit-label]').forEach((el) => (el.textContent = unit));
      this.querySelectorAll('.csb__dim input').forEach((el) => { el.step = unit === 'cm' ? '0.1' : '1'; el.inputMode = unit === 'cm' ? 'decimal' : 'numeric'; });
      var q = quote({ unit: unit, width: this.val('csb-width'), height: this.val('csb-height'), depth: this.val('csb-depth') }, this.cfg, this.variants);
      ['width', 'depth', 'height'].forEach((k) => {
        var err = q.errors.find((e) => e.field === k);
        var input = this.querySelector('[name="csb-' + k + '"]');
        var msg = this.querySelector('[data-csb-error="' + k + '"]');
        var show = err && this.touched['csb-' + k];
        input.setAttribute('aria-invalid', err ? 'true' : 'false');
        msg.textContent = show ? err.message : '';
        msg.hidden = !show;
      });
      var volErr = q.errors.find((e) => e.field === 'volume');
      var volMsg = this.querySelector('[data-csb-error="volume"]');
      volMsg.textContent = volErr ? volErr.message : '';
      volMsg.hidden = !volErr;

      this.propsBox.innerHTML = '';
      // Let other parts of the page (e.g. the made-to-order suggestion) react to the quote.
      this.dispatchEvent(new CustomEvent('csb:quote', { bubbles: true, detail: { ok: q.ok, dimsMm: q.dimsMm, price: q.ok && q.variant ? q.variant.price : null, colour: this.val('csb-colour') } }));
      if (!q.ok) {
        this.priceEl.textContent = this.priceEl.dataset.fromLabel;
        this.volumeEl.textContent = '';
        this.submit.setAttribute('aria-disabled', 'true');
        return false;
      }
      this.idInput.value = q.variant.id;
      this.idInput.disabled = false;
      this.priceEl.textContent = money(q.variant.price);
      this.volumeEl.textContent = 'Volume ' + q.volumeM3.toFixed(3) + ' m³ · price band ' + q.variant.title;
      this.submit.removeAttribute('aria-disabled');
      var props = lineProperties(q, { colour: this.val('csb-colour'), lid: this.val('csb-lid'), leadTime: this.leadTime, notes: this.val('csb-notes') }, this.cfg);
      Object.keys(props).forEach((k) => {
        var i = document.createElement('input');
        i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = props[k];
        this.propsBox.appendChild(i);
      });
      return true;
    }
  }
  if (!customElements.get('custom-size-builder')) customElements.define('custom-size-builder', CustomSizeBuilder);
})(typeof window !== 'undefined' ? window : globalThis);
